import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { uploadDocument, listDocuments, deleteDocument, searchDocuments, updateDocument } from '../services/api';
import toast from 'react-hot-toast';

const DOC_TYPES = ['contract', 'nda', 'lease', 'employment', 'letter', 'court_document', 'other'];
const JURISDICTIONS = ['HK', 'UK', 'US', 'SG', 'AU', 'CN', 'Other'];

const RISK_COLOR: Record<string, string> = {
  low: 'text-green-600 bg-green-50 border-green-200',
  medium: 'text-amber-600 bg-amber-50 border-amber-200',
  high: 'text-orange-600 bg-orange-50 border-orange-200',
  critical: 'text-red-600 bg-red-50 border-red-200',
};

export default function DocumentsPage() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef<HTMLInputElement>(null);
  const [docs, setDocs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [meta, setMeta] = useState({ label: '', doc_type: 'contract', jurisdiction: 'HK', tags: '' });
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [editingDoc, setEditingDoc] = useState<string | null>(null);
  const [editTags, setEditTags] = useState('');

  const load = useCallback(async () => {
    if (!currentUser) return;
    try {
      const token = await currentUser.getIdToken();
      const res = await listDocuments(token);
      setDocs(res.documents || []);
    } catch { /* ok */ } finally { setLoading(false); }
  }, [currentUser]);

  useEffect(() => { load(); }, [load]);

  const handleSearch = async () => {
    if (!currentUser || !searchQuery.trim()) { load(); return; }
    setSearching(true);
    try {
      const token = await currentUser.getIdToken();
      const res = await searchDocuments(token, searchQuery);
      setDocs(res.documents || []);
    } catch { toast.error('Search failed'); }
    finally { setSearching(false); }
  };

  const handleUpload = async (files: FileList | File[]) => {
    if (!currentUser) return;
    setUploading(true);
    const fileArray = Array.from(files);
    let success = 0;
    for (const file of fileArray) {
      try {
        const token = await currentUser.getIdToken();
        const label = fileArray.length === 1 && meta.label ? meta.label : file.name;
        await uploadDocument(token, file, { ...meta, label, tags: meta.tags });
        success++;
      } catch (e: any) {
        toast.error(`Failed: ${file.name} — ${e.message || 'error'}`);
      }
    }
    if (success > 0) {
      toast.success(`${success} document${success > 1 ? 's' : ''} uploaded!`);
      setMeta({ label: '', doc_type: 'contract', jurisdiction: 'HK', tags: '' });
      load();
    }
    setUploading(false);
  };

  const handleDelete = async (docId: string, label: string) => {
    if (!currentUser || !window.confirm(`Delete "${label}"?`)) return;
    try {
      const token = await currentUser.getIdToken();
      await deleteDocument(token, docId);
      toast.success('Deleted');
      setDocs((prev) => prev.filter((d) => d.doc_id !== docId));
    } catch (e: any) { toast.error(e.message || 'Delete failed'); }
  };

  const handleSaveTags = async (docId: string) => {
    if (!currentUser) return;
    try {
      const token = await currentUser.getIdToken();
      const tags = editTags.split(',').map((t) => t.trim()).filter(Boolean);
      await updateDocument(token, docId, { tags });
      toast.success('Tags updated');
      setEditingDoc(null);
      load();
    } catch (e: any) { toast.error(e.message || 'Update failed'); }
  };

  return (
    <div className="max-w-4xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Document Vault</h1>
        <p className="text-gray-500 text-sm mt-1">Upload contracts and legal documents for AI analysis.</p>
      </div>

      {/* Search bar */}
      <div className="flex gap-2">
        <input type="text" value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          placeholder="Search documents by name, type, tags, or summary..."
          className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
        <button onClick={handleSearch} disabled={searching}
          className="px-5 py-2.5 bg-slate-700 text-white rounded-lg text-sm font-medium hover:bg-slate-600 disabled:opacity-50 transition-colors">
          {searching ? '...' : 'Search'}
        </button>
        {searchQuery && (
          <button onClick={() => { setSearchQuery(''); load(); }}
            className="px-3 py-2.5 border border-gray-300 text-gray-500 rounded-lg text-sm hover:bg-gray-50 transition-colors">
            Clear
          </button>
        )}
      </div>

      {/* Upload zone */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-4">
        <h2 className="font-semibold text-gray-800">Upload Document</h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Label</label>
            <input type="text" value={meta.label} onChange={(e) => setMeta((m) => ({ ...m, label: e.target.value }))}
              placeholder="e.g. Employment contract 2024"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Document Type</label>
            <select value={meta.doc_type} onChange={(e) => setMeta((m) => ({ ...m, doc_type: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              {DOC_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Jurisdiction</label>
            <select value={meta.jurisdiction} onChange={(e) => setMeta((m) => ({ ...m, jurisdiction: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              {JURISDICTIONS.map((j) => <option key={j} value={j}>{j}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Tags</label>
            <input type="text" value={meta.tags} onChange={(e) => setMeta((m) => ({ ...m, tags: e.target.value }))}
              placeholder="e.g. urgent, renewal, 2024"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
        </div>

        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); if (e.dataTransfer.files.length) handleUpload(e.dataTransfer.files); }}
          onClick={() => fileRef.current?.click()}
          className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-colors ${
            dragOver ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50/30'
          }`}>
          <input ref={fileRef} type="file" accept=".pdf,.docx,.doc,.txt,.msg,.eml,.zip,.xlsx,.xls,.rtf,.html,.htm" className="hidden" multiple
            onChange={(e) => { if (e.target.files?.length) handleUpload(e.target.files); }} />
          <div className="text-3xl mb-2">{uploading ? '⏳' : '📤'}</div>
          <p className="font-medium text-gray-700 text-sm">{uploading ? 'Uploading and parsing...' : 'Drop files here or click to browse'}</p>
          <p className="text-xs text-gray-400 mt-1">PDF, DOCX, TXT, MSG, EML, ZIP, XLSX, RTF, HTML · max 20 MB each · multiple files supported</p>
        </div>
      </div>

      {/* Document list */}
      {loading ? (
        <div className="text-center py-12 text-gray-400">Loading documents...</div>
      ) : docs.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <div className="text-4xl mb-2">📂</div>
          <p>{searchQuery ? 'No documents match your search' : 'No documents yet — upload one above'}</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="font-semibold text-gray-800">
              {docs.length} Document{docs.length !== 1 ? 's' : ''}
              {searchQuery && <span className="text-gray-400 font-normal"> matching "{searchQuery}"</span>}
            </h2>
          </div>
          <div className="divide-y divide-gray-50">
            {docs.map((d) => (
              <div key={d.doc_id} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-start gap-3 min-w-0">
                    <span className="text-2xl mt-0.5">📄</span>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">{d.label}</p>
                      <p className="text-xs text-gray-400">{d.doc_type} · {d.jurisdiction} · {d.char_count?.toLocaleString()} chars · {d.uploaded_at?.slice(0, 10)}</p>
                      {d.summary && <p className="text-xs text-gray-500 mt-1 line-clamp-1">{d.summary}</p>}
                      {/* Tags */}
                      {editingDoc === d.doc_id ? (
                        <div className="flex items-center gap-2 mt-2">
                          <input type="text" value={editTags} onChange={(e) => setEditTags(e.target.value)}
                            placeholder="tag1, tag2, tag3"
                            className="px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 w-48"
                            onKeyDown={(e) => e.key === 'Enter' && handleSaveTags(d.doc_id)} />
                          <button onClick={() => handleSaveTags(d.doc_id)}
                            className="text-xs text-blue-600 hover:text-blue-800 font-medium">Save</button>
                          <button onClick={() => setEditingDoc(null)}
                            className="text-xs text-gray-400 hover:text-gray-600">Cancel</button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                          {(d.tags || []).map((tag: string) => (
                            <span key={tag} className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full border border-blue-200">{tag}</span>
                          ))}
                          <button onClick={() => { setEditingDoc(d.doc_id); setEditTags((d.tags || []).join(', ')); }}
                            className="text-xs text-gray-400 hover:text-blue-600 transition-colors">
                            {d.tags?.length ? 'edit tags' : '+ add tags'}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {d.risk_level && (
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${RISK_COLOR[d.risk_level]}`}>
                        {d.risk_score}/100
                      </span>
                    )}
                    <button onClick={() => navigate(`/app/review?doc=${d.doc_id}`)}
                      className="px-3 py-1.5 bg-blue-700 text-white rounded-lg text-xs font-medium hover:bg-blue-600 transition-colors">
                      Analyse
                    </button>
                    {d.filename?.toLowerCase().endsWith('.pdf') && (
                      <button onClick={() => navigate(`/app/documents/${d.doc_id}/annotate`)}
                        className="px-3 py-1.5 border border-blue-200 text-blue-700 rounded-lg text-xs font-medium hover:bg-blue-50 transition-colors">
                        Annotate{d.annotation_count ? ` (${d.annotation_count})` : ''}
                      </button>
                    )}
                    <button onClick={() => handleDelete(d.doc_id, d.label)}
                      className="px-3 py-1.5 border border-gray-300 text-gray-500 rounded-lg text-xs hover:bg-red-50 hover:text-red-600 hover:border-red-300 transition-colors">
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
